/* eslint-disable no-shadow */
/* eslint-disable max-len */
import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import cx from 'classnames';
import moment from 'moment';
import isEqual from 'lodash/isEqual';
import appContext from '../../constants/appContext';
import Button from '../Button';
import { createChainedFunction, getPageContext, focusTrap } from '../../utils';
import AriaLiveRegion from '../AriaLiveRegion';
import Modal from '../Modal';
import ErrorModal from '../ErrorModal';
import NotifyPreState from './NotifyPreState';
import NotifyPostState from './NotifyPostState';
import TranslationProvider, { getTranslation } from '../../providers/TranslationProvider';
import { deleteNotify, postNotify } from '../../api';
import Logger from '../../utils/logger';
import { DATE_TIME_FORMAT } from '../../constants/dateFormats';

import './Notify.scss';

import {
  SERVICE_TYPES,
  NOOP,
  CONSTANTS,
  getAuthModalStyles,
} from './notifyUtils';
import { setSelectedCoreISODate, setSelectedCoreSeats } from '../../reducers/resyCoreSlice';
import { sanitizeTargetUrl } from '../../utils/sanitizeTargetUrl';

const {
  CANCEL_SUCCESS_MESSAGE,
  SET_NOTIFY_SUCCESS_MESSAGE,
  SET_NOTIFY,
  TABLES_AVAILABLE,
  SET_NOTIFY_SUCCESS,
  BUTTON_ID_CHANGE,
  BUTTON_ID_CONFIRM,
  BUTTON_ID_CLOSE,
} = CONSTANTS;

const TIME_FORMAT = 'HH:mm:ss';

const NOTIFY_EVENTS = {
  CHANGE: 'change notify',
  REMOVE: 'remove notify',
  SET: 'set notify',
};

class Notify extends Component {
  state = {
    // eslint-disable-next-line react/destructuring-assignment
    isOpen: this.props.isOpen,
    AriaLiveRegionMessage: null,
    isErrorModalOpen: false,
    notifyEnd: '',
    notifyStart: '',
    notifyFlowState: SET_NOTIFY,
    errorMessage: null,
  }

  componentDidMount() {
    const {
      $scope,
      signal,
    } = this.props;

    // tying the modal into the angular controller
    // is a challenge
    // i came up with this, which in combo with
    // the prop openModal does work
    // making this purely prop-driven did not work
    // because angular doesn't reliably digest/refresh
    // when a value is changed

    // As Angular.js migration occurs, need to listen to
    // both Angular $on and Redux signal for this shared component.
    $scope.$on(
      'TOGGLE_MODAL',
      this.toggleModal
    );
    if (signal.name === 'TOGGLE_MODAL') {
      this.toggleModal(null, signal.args);
    }

    this.setNotifyOptions();
  }

  componentDidUpdate(prev) {
    const {
      serviceTypeId: prevServiceTypeId,
      activeNotifications: prevActionNotification,
      signal: prevSignal,
    } = prev;

    const {
      serviceTypeId,
      activeNotifications,
      signal,
    } = this.props;

    if (prevServiceTypeId !== serviceTypeId) {
      this.setNotifyOptions();
    }

    if (activeNotifications !== prevActionNotification) {
      this.setNotifyOptions();
    }

    if (!isEqual(prevSignal, signal) && signal.name === 'TOGGLE_MODAL') {
      this.toggleModal(null, signal.args);
    }
  }

  toggleModal = (e, val) => {
    const { isErrorModalOpen } = this.state;
    const isOpen = !!val;

    if (isErrorModalOpen) {
      return;
    }

    this.setState({
      isOpen,
      AriaLiveRegionMessage: undefined,
      notifyFlowState: SET_NOTIFY,
    });
  }

  afterCloseModal = () => {
    const { onCloseNotifyModal } = this.props;

    onCloseNotifyModal();
    // eslint-disable-next-line react/destructuring-assignment
    this.props.focusTrap?.focusPage?.();
  }

  afterOpenModal = () => {
    // eslint-disable-next-line react/destructuring-assignment
    this.props.focusTrap?.focusModal?.();
  }

  cancelNotify = async () => {
    const {
      selectedDate,
      selectedQuantity,
      serviceTypeId,
      venueId,
    } = this.props;

    const date = moment(selectedDate).format('YYYY-MM-DD');

    const notifyConfig = {
      venue_id: venueId,
      day: date,
      num_seats: selectedQuantity,
      service_type_id: serviceTypeId,
    };

    try {
      await deleteNotify(notifyConfig);
      this.cancelNotifySuccess();
    } catch (error) {
      Logger.error('[Notify] error removing a notify');
    }
  }

  // AMPLITUDE EVENT TRACKING
  sendNotifyTrackingEvent = (eventName) => {
    if (!eventName) return;
    const {
      Analytics,
      analyticsInfo = {},
      eventName: event,
      searchSessionId,
      selectedDate,
      selectedQuantity,
      venueName,
    } = this.props;
    const { notifyStart } = this.state;

    const notifyEventProperties = {
      'cc required': analyticsInfo.ccRequired,
      city: analyticsInfo.location?.name,
      context: getPageContext(window.location.pathname),
      'party size': selectedQuantity,
      venue: venueName,
      'venue is GDA': analyticsInfo.venueIsGda,
      ...(selectedDate && notifyStart && {
        time: moment(`${moment(selectedDate).format('YYYY-MM-DD')} ${notifyStart}`).format(DATE_TIME_FORMAT),
      }),
      'is event': !!event,
    };

    if (eventName === NOTIFY_EVENTS.SET) {
      notifyEventProperties['lead time'] = moment(selectedDate).diff(moment().startOf('day'), 'days');
      notifyEventProperties.searchSessionId = searchSessionId;
    }

    Analytics.event(eventName, notifyEventProperties);
  }

  cancelNotifySuccess = () => {
    const chained = createChainedFunction(this.syncWithParent, this.closeModalWithDelay);
    this.setState(
      {
        AriaLiveRegionMessage: CANCEL_SUCCESS_MESSAGE,
      },
      chained
    );
    this.sendNotifyTrackingEvent(NOTIFY_EVENTS.REMOVE);
    this.loadReservations();
  }

  checkForMatches = () => {
    const {
      reservations,
      selectedDate,
    } = this.props;

    const {
      notifyStart,
      notifyEnd,
    } = this.state;

    let reservationTime;
    let found = false;

    const date = moment(selectedDate).format('YYYY-MM-DD');
    const start = moment(`${date} ${notifyStart}`);
    const end = moment(`${date} ${notifyEnd}`);

    // midnight must be tomorrow for isBetween() comparison to make sense
    if (notifyEnd === '00:00:00') {
      end.add(1, 'day');
    }
    reservations.forEach(
      (servicePeriod) => {
        servicePeriod.available.forEach(
          (resy) => {
            reservationTime = moment(resy.time);
            if (reservationTime.isBetween(start, end, null, '[]')) {
              found = true;
            }
          }
        );
      }
    );
    return found;
  }

  closeModal = () => {
    const {
      openModal,
    } = this.props;

    openModal(false);
  }

  closeModalWithDelay = () => {
    setTimeout(this.closeModal, 100);
  }

  closeModalAndReload = async () => {
    const { clearNotifyParams, $route } = this.props;
    await clearNotifyParams();
    this.closeModal();
    $route.reload();
  }

  createNotify = async (amplitudeEvent) => {
    const {
      selectedDate,
      selectedQuantity,
      serviceTypeId,
      venueId,
    } = this.props;

    const {
      notifyEnd,
      notifyStart,
    } = this.state;

    const day = moment(selectedDate).format('YYYY-MM-DD');

    const notifyConfig = {
      struct_data: {
        notifies: [{
          venue_id: venueId,
          day,
          time_preferred_start: notifyStart,
          time_preferred_end: notifyEnd,
          num_seats: selectedQuantity,
          service_type_id: serviceTypeId,
        }],
      },
    };

    try {
      await postNotify(notifyConfig);
      this.createNotifySuccess(amplitudeEvent);
    } catch (error) {
      this.setState({
        errorMessage: error.data?.message,
        isErrorModalOpen: true,
      });
      Logger.error('[Notify] error posting notify data');
    }
  }

  createNotifySuccess = (eventName) => {
    const {
      isGda,
      selectedDate,
    } = this.props;

    const {
      notifyStart, notifyEnd,
    } = this.state;
    const notifyType = getTranslation({
      translationKey: isGda ? 'priority_notify_on' : 'notify_on',
      fallback: isGda ? 'Priority Notify On' : 'Notify On',
    });
    const formattedDate = moment(selectedDate).format('YYYY-MM-DD');
    this.sendNotifyTrackingEvent(eventName);
    focusTrap.focusModal();
    this.setState(
      {
        AriaLiveRegionMessage: `${notifyType}; ${SET_NOTIFY_SUCCESS_MESSAGE(
          moment(`${formattedDate} ${notifyStart}`).format('LLLL'),
          moment(`${formattedDate} ${notifyEnd}`).format('LLLL')
        )}`,
        notifyFlowState: SET_NOTIFY_SUCCESS,
      },
      this.syncWithParent
    );
    this.loadReservations();
  }

  loadReservations = () => {
    const {
      loadReservations,
      selectedDate,
      selectedQuantity,
      venueId,
    } = this.props;

    loadReservations(
      venueId, selectedDate, selectedQuantity
    );
  }

  onClickCancelNotify = async () => {
    const {
      $route,
      clearNotifyParams,
    } = this.props;

    await this.cancelNotify();
    await clearNotifyParams();
    $route.reload();
  }

  onClickSetNotify = (event) => {
    const setNotifyButton = event.target.dataset.testId;
    let amplitudeEvent;

    if (setNotifyButton === BUTTON_ID_CHANGE) {
      amplitudeEvent = NOTIFY_EVENTS.CHANGE;
    } else if (setNotifyButton === BUTTON_ID_CONFIRM) {
      amplitudeEvent = NOTIFY_EVENTS.SET;
    }

    if (this.checkForMatches()) {
      this.setState({
        notifyFlowState: TABLES_AVAILABLE,
        AriaLiveRegionMessage: getTranslation({ translationKey: 'tables_available', fallback: 'Tables Available!' }),
      });
      return;
    }

    this.createNotify(amplitudeEvent);
  }

  populateTimeSelectValues = () => {
    const {
      serviceTypeId,
    } = this.props;
    const serviceType = SERVICE_TYPES[serviceTypeId];

    let timeDisplay;
    let timeValue;

    const range = [];
    const startTime = moment(serviceType.startTime, TIME_FORMAT);
    const endTime = moment(serviceType.endTime, TIME_FORMAT);
    const tempTime = moment(startTime);
    while (tempTime <= endTime) {
      timeDisplay = tempTime.format('LT');
      timeValue = tempTime.format(TIME_FORMAT);

      range.push({
        label: timeDisplay,
        value: timeValue,
        key: timeValue,
      });
      tempTime.add(30, 'minutes');
    }

    return range;
  }

  selectEnd = (e) => {
    const {
      notifyStart,
    } = this.state;

    this.setStartEnd(notifyStart, e.currentTarget.value);
  }

  selectStart = (e) => {
    const {
      notifyEnd,
    } = this.state;

    this.setStartEnd(e.currentTarget.value, notifyEnd);
  }

  setStartEnd = (start, end) => {
    const notifyStartTime = moment(start, TIME_FORMAT);
    const notifyEndTime = moment(end, TIME_FORMAT);
    // don't swap times if after 5am to support after midnight
    if (
      notifyStartTime.isAfter(notifyEndTime)
      && notifyEndTime.isAfter(moment('05:00', 'HH:mm'))
    ) {
      this.setState({
        notifyStart: end,
        notifyEnd: start,
      });
    } else {
      this.setState({
        notifyStart: start,
        notifyEnd: end,
      });
    }
  }

  setNotifyOptions = () => {
    const {
      activeNotifications,
      serviceTypeId,
    } = this.props;

    if (!Number.isInteger(serviceTypeId) || !activeNotifications) {
      return;
    }

    let notifyStart;
    let notifyEnd;
    const notifyTimeOptions = this.populateTimeSelectValues();

    if (activeNotifications[serviceTypeId]) {
      notifyStart = activeNotifications[serviceTypeId].start;
      notifyEnd = activeNotifications[serviceTypeId].end;

      // handle after midnight being outside of our hardcoded notify time range
      /* istanbul ignore else */
      if (notifyTimeOptions.filter((option) => option.value === notifyEnd).length === 0) {
        notifyTimeOptions.push({
          label: moment(notifyEnd, TIME_FORMAT).format('LT'),
          value: notifyEnd,
        });
      }
    } else {
      notifyStart = notifyTimeOptions[0].value;
      notifyEnd = notifyTimeOptions[notifyTimeOptions.length - 1].value;
    }

    this.setState({
      notifyStart,
      notifyEnd,
      notifyTimeOptions,
    });
  }

  syncWithParent = () => {
    const {
      $window: {
        parent: {
          postMessage,
        },
      },
      activeNotifications,
      venueId,
    } = this.props;

    postMessage({
      event: 'onNotifySync',
      value: activeNotifications,
      venueId,
    }, '*');
  }

  showAvailability = () => {
    const {
      context,
      selectedDate,
      selectedQuantity,
      setSelectedCoreISODate,
      setSelectedCoreSeats,
      venueCitySlug,
      venueId,
      venueUrl,
    } = this.props;

    if (context === appContext.WIDGET) {
      setSelectedCoreISODate(selectedDate);
      setSelectedCoreSeats(selectedQuantity);
      const currentHash = window.location.hash.split('?')[0];
      const venueHash = `#/venues/${venueId}`;
      if (currentHash === venueHash) {
        this.closeModal();
      } else {
        window.location.hash = venueHash;
      }
    } else {
      const sanitizedUrl = sanitizeTargetUrl(`${window.location.origin}/cities/${venueCitySlug}/venues/${venueUrl}?date=${selectedDate}&seats=${selectedQuantity}`);
      if (!venueCitySlug || !venueUrl || !sanitizedUrl.includes(venueCitySlug) || !sanitizedUrl.includes(venueUrl)) {
        this.closeModal();
      } else {
        window.location.href = sanitizedUrl;
      }
    }
  }

  onCloseErrorModal = () => {
    this.setState({ isErrorModalOpen: false });
  }

  renderCreateNotifyError = () => {
    const { errorMessage } = this.state;

    const message = (
      <TranslationProvider translationKey="notify_create_error">
        <div role="alert">
          {errorMessage}
        </div>
      </TranslationProvider>
    );

    return (
      <ErrorModal
        message={message}
        modalStyles={{ content: { fontSize: '16px' } }}
        onErrorModalClose={this.onCloseErrorModal}
        primaryCTAKey="dismiss"
        primaryCTAOnClick={this.onCloseErrorModal}
        primaryCTAText="Dismiss"
        primaryCTAVariant="primary"
      />
    );
  }

  render() {
    const {
      Analytics,
      activeNotifications,
      context,
      eventName,
      isGda,
      locationCode,
      matchMedia,
      scrollTop,
      selectedDate,
      selectedQuantity,
      serviceTypeId,
      venueName,
      venueUrl,
      deviceId,
    } = this.props;

    const {
      AriaLiveRegionMessage,
      isErrorModalOpen,
      isOpen,
      notifyStart,
      notifyEnd,
      notifyFlowState,
      notifyTimeOptions,
    } = this.state;

    if (!isOpen) {
      return null;
    }

    const isPostState = notifyFlowState === TABLES_AVAILABLE || notifyFlowState === SET_NOTIFY_SUCCESS;
    const statefulCloseModal = notifyFlowState === SET_NOTIFY_SUCCESS ? this.closeModalAndReload : this.closeModal;
    let parentSelector;

    if (context === appContext.WIDGET) {
      parentSelector = () => document.getElementById('page-wrapper');
    }
    const contentLabelText = isPostState ? getTranslation({ translationKey: 'tables_available', fallback: 'Tables Available!' }) : getTranslation({
      translationKey: isGda ? 'set_priority_notify' : 'set_notify',
      fallback: isGda ? 'Set Priority Notify' : 'Set Notify',
    });
    return (
      <Modal
        parentSelector={parentSelector}
        isOpen={isOpen}
        contentLabel={contentLabelText}
        shouldCloseOnEsc
        shouldCloseOnOverlayClick
        onAfterClose={this.afterCloseModal}
        onAfterOpen={this.afterOpenModal}
        onRequestClose={this.closeModal}
        styles={getAuthModalStyles(matchMedia, scrollTop)}
      >
        {isErrorModalOpen && this.renderCreateNotifyError()}
        <div className={cx('Notify', { Notify__Gda: isGda })} data-testid={isGda ? 'Notify__Gda' : undefined}>
          <AriaLiveRegion id="notify-AriaLiveRegion" politeness="assertive">{AriaLiveRegionMessage}</AriaLiveRegion>
          <div className="Notify__nav">
            <Button
              aria-label={getTranslation({ translationKey: 'close_notify_modal', fallback: 'Close Notify Modal' })}
              variant="close"
              onClick={statefulCloseModal}
              data-test-id={BUTTON_ID_CLOSE}
            />
          </div>
          {isPostState ? (
            <NotifyPostState
              Analytics={Analytics}
              matchMedia={matchMedia}
              closeModalAndReload={this.closeModalAndReload}
              context={context}
              isGda={isGda}
              locationCode={locationCode}
              notifyStart={notifyStart}
              notifyEnd={notifyEnd}
              notifyFlowState={notifyFlowState}
              selectedDate={selectedDate}
              selectedQuantity={selectedQuantity}
              showAvailability={this.showAvailability}
              venueName={venueName}
              venueUrl={venueUrl}
              eventName={eventName}
              deviceId={deviceId}
            />
          ) : (
            <NotifyPreState
              activeNotifications={activeNotifications}
              isGda={isGda}
              notifyStart={notifyStart}
              notifyEnd={notifyEnd}
              notifyTimeOptions={notifyTimeOptions}
              onClickCancelNotify={this.onClickCancelNotify}
              onClickSetNotify={this.onClickSetNotify}
              selectStart={this.selectStart}
              selectEnd={this.selectEnd}
              selectedDate={selectedDate}
              selectedQuantity={selectedQuantity}
              serviceTypeId={serviceTypeId}
              venueUrl={venueUrl}
              venueName={venueName}
              eventName={eventName}
            />
          )}
        </div>
      </Modal>
    );
  }
}

const notifyPropTypes = {
  $route: PropTypes.shape({
    reload: PropTypes.func,
  }).isRequired,
  $scope: PropTypes.shape({
    $on: PropTypes.func,
  }).isRequired,
  $window: PropTypes.shape({
    parent: PropTypes.shape({
      postMessage: PropTypes.func,
    }),
  }).isRequired,
  activeNotifications: PropTypes.shape({
    [PropTypes.number]: PropTypes.shape({
      start: PropTypes.string,
      end: PropTypes.string,
    }),
  }),
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
  clearNotifyParams: PropTypes.func,
  context: PropTypes.oneOf(['consumer', 'widget']).isRequired,
  focusTrap: PropTypes.shape({
    focusModal: PropTypes.func,
    focusPage: PropTypes.func,
  }),
  isGda: PropTypes.bool,
  isOpen: PropTypes.bool,
  loadReservations: PropTypes.func,
  locationCode: PropTypes.string,
  matchMedia: PropTypes.shape({
    breakpoints: PropTypes.shape({
      sm: PropTypes.bool,
      xs: PropTypes.bool,
    }),
  }).isRequired,
  onCloseNotifyModal: PropTypes.func,
  openModal: PropTypes.func.isRequired,
  reservations: PropTypes.arrayOf(PropTypes.shape({
    available: PropTypes.arrayOf(
      PropTypes.shape({
        time: PropTypes.string,
      })
    ),
  })),
  scrollTop: PropTypes.number,
  searchSessionId: PropTypes.string,
  selectedDate: PropTypes.string,
  selectedQuantity: PropTypes.number,
  setSelectedCoreISODate: PropTypes.func.isRequired,
  setSelectedCoreSeats: PropTypes.func.isRequired,
  serviceTypeId: PropTypes.number,
  signal: PropTypes.shape({ name: PropTypes.string, args: PropTypes.number }).isRequired,
  venueCitySlug: PropTypes.string,
  venueId: PropTypes.number,
  venueUrl: PropTypes.string,
  venueName: PropTypes.string,
  eventName: PropTypes.string,
  deviceId: PropTypes.string.isRequired,
  analyticsInfo: PropTypes.shape({
    ccRequired: PropTypes.bool.isRequired,
    location: PropTypes.shape({ code: PropTypes.string.isRequired }),
    venueIsGda: PropTypes.bool.isRequired,
  }).isRequired,
};

Notify.propTypes = notifyPropTypes;

Notify.defaultProps = {
  activeNotifications: undefined,
  clearNotifyParams: NOOP,
  focusTrap: {
    focusModal: NOOP,
    focusPage: NOOP,
  },
  isGda: false,
  isOpen: false,
  loadReservations: undefined,
  locationCode: undefined,
  onCloseNotifyModal: NOOP,
  reservations: [],
  scrollTop: undefined,
  searchSessionId: '',
  selectedDate: undefined,
  selectedQuantity: undefined,
  serviceTypeId: undefined,
  venueId: undefined,
  venueCitySlug: undefined,
  venueName: undefined,
  venueUrl: undefined,
  eventName: undefined,
};

Notify.displayName = 'Notify';

const mapStateToProps = ({
  matchMedia, resyLocations, signal, resyConfig, venueReservations, venueSearch,
}) => ({
  matchMedia,
  locationCode: resyLocations.selected?.code,
  signal,
  deviceId: resyConfig.deviceId,
  analyticsInfo: venueReservations?.data?.analytics,
  searchSessionId: venueSearch?.metadata?.searchSessionId,
});

const mapDispatchToProps = {
  setSelectedCoreISODate,
  setSelectedCoreSeats,
};

export { notifyPropTypes, Notify as ComponentUnderTest };
export default connect(mapStateToProps, mapDispatchToProps)(Notify);
